/**
 * Dependency-free PDF writer for one full-page image. The page is sized from
 * the image's pixel size and a DPI, the pixels go in as a zlib-compressed
 * RGB stream (CompressionStream, falling back to uncompressed), and the
 * cross-reference table is computed from the byte offsets, so any PDF reader
 * opens it. Good for print: the certificate renders at 300 dpi.
 */

const enc = new TextEncoder()

function pdfString(s: string): string {
  return '(' + s.replace(/[\\()]/g, (m) => '\\' + m).replace(/[^\x20-\x7e]/g, '') + ')'
}

async function deflate(bytes: Uint8Array): Promise<{ data: Uint8Array; filter: string } | null> {
  if (typeof CompressionStream === 'undefined') return null
  try {
    const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new CompressionStream('deflate'))
    const data = new Uint8Array(await new Response(stream).arrayBuffer())
    return { data, filter: ' /Filter /FlateDecode' }
  } catch {
    return null
  }
}

/** RGB bytes from a canvas, with any transparency composited over white. */
export function canvasRgb(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas unavailable')
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const rgb = new Uint8Array(canvas.width * canvas.height * 3)
  for (let i = 0, o = 0; i < data.length; i += 4, o += 3) {
    const a = data[i + 3] / 255
    rgb[o] = Math.round(data[i] * a + 255 * (1 - a))
    rgb[o + 1] = Math.round(data[i + 1] * a + 255 * (1 - a))
    rgb[o + 2] = Math.round(data[i + 2] * a + 255 * (1 - a))
  }
  return rgb
}

export interface PdfOptions {
  /** Pixels per inch used to size the page; 300 by default. */
  dpi?: number
  title?: string
}

/** Build a one-page PDF whose page is exactly the image. */
export async function imageToPdf(rgb: Uint8Array, width: number, height: number, opts: PdfOptions = {}): Promise<Blob> {
  const dpi = opts.dpi ?? 300
  const w = +((width * 72) / dpi).toFixed(2)
  const h = +((height * 72) / dpi).toFixed(2)
  const compressed = await deflate(rgb)
  const image = compressed?.data ?? rgb
  const filter = compressed?.filter ?? ''
  const content = enc.encode(`q ${w} 0 0 ${h} 0 0 cm /Im0 Do Q`)

  const parts: Uint8Array[] = []
  let length = 0
  const offsets: number[] = []
  const push = (chunk: Uint8Array | string) => {
    const bytes = typeof chunk === 'string' ? enc.encode(chunk) : chunk
    parts.push(bytes)
    length += bytes.length
  }
  const obj = (n: number, body: string, stream?: Uint8Array) => {
    offsets[n] = length
    push(`${n} 0 obj\n${body}\n`)
    if (stream) {
      push('stream\n')
      push(stream)
      push('\nendstream\n')
    }
    push('endobj\n')
  }

  push('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n')
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>')
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>')
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${w} ${h}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`)
  obj(4, `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8${filter} /Length ${image.length} >>`, image)
  obj(5, `<< /Length ${content.length} >>`, content)
  obj(6, `<< /Producer (ColorMark) /Creator (colormark.org)${opts.title ? ' /Title ' + pdfString(opts.title) : ''} >>`)

  const xref = length
  push(`xref\n0 7\n0000000000 65535 f \n`)
  for (let n = 1; n <= 6; n++) push(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`)
  push(`trailer\n<< /Size 7 /Root 1 0 R /Info 6 0 R >>\nstartxref\n${xref}\n%%EOF\n`)

  return new Blob(parts as BlobPart[], { type: 'application/pdf' })
}

/** Convenience: canvas straight to a print-ready PDF. */
export function canvasToPdf(canvas: HTMLCanvasElement, opts: PdfOptions = {}): Promise<Blob> {
  return imageToPdf(canvasRgb(canvas), canvas.width, canvas.height, opts)
}
